import React from 'react';
import './TimeSlotSelector.css';

interface TimeSlot {
  time: string;
  available: boolean;
}

interface TimeSlotSelectorProps {
  selectedTime: string;
  onTimeSelect: (time: string) => void;
  availableSlots?: TimeSlot[];
  existingBookings?: { id: string; time: string; serviceName?: string | null; duration: number }[];
  loading?: boolean;
}

const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
  selectedTime,
  onTimeSelect,
  availableSlots,
  existingBookings = [],
  loading = false
}) => {
  // Default time slots if none provided
  const defaultSlots: TimeSlot[] = [
    { time: '09:00', available: true },
    { time: '10:00', available: true },
    { time: '11:00', available: true },
    { time: '12:00', available: true },
    { time: '13:00', available: false }, // Lunch break
    { time: '14:00', available: true },
    { time: '15:00', available: true },
    { time: '16:00', available: true },
    { time: '17:00', available: true },
  ];

  const slots = availableSlots || defaultSlots;
  const hasAvailableSlot = slots.some(slot => slot.available);

  const formatTimeLabel = (time: string) => {
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(Number(hours), Number(minutes), 0, 0);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="time-slot-selector">
      <h3 className="time-slot-title">Select Time</h3>
      {loading && (
        <div className="time-slot-status">Checking availability...</div>
      )}
      {!loading && existingBookings.length > 0 && (
        <div className="time-slot-booked-list">
          <p className="booked-list-title">Already booked:</p>
          <ul>
            {existingBookings.map(({ id, time, serviceName, duration }) => (
              <li key={id}>
                <span className="booked-time">{formatTimeLabel(time)}</span>
                {serviceName && <span className="booked-service"> · {serviceName}</span>}
                <span className="booked-duration"> ({duration} hours)</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="time-slot-grid">
        {slots.map(({ time, available }) => (
          <button
            key={time}
            type="button"
            className={`time-slot ${selectedTime === time ? 'selected' : ''} ${!available ? 'unavailable' : ''}`}
            onClick={() => available && onTimeSelect(time)}
            disabled={!available}
          >
            {time}
          </button>
        ))}
      </div>
      {!loading && !hasAvailableSlot && (
        <div className="time-slot-status unavailable">
          No slots are available for this date. Please select another day.
        </div>
      )}
    </div>
  );
};

export default TimeSlotSelector;